import { ArrowRight, TriangleAlert } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from '@kostbar/i18n'
import type {
  ReportDimension,
  ReportMeasure,
  ReportRow,
  ReportTotals,
} from '@/features/reports/api/use-sales-report-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ShareBar } from '@/features/reports/components/share-bar'
import { formatCurrency, formatNumber } from '@/features/reports/lib/format'
import { formatRowLabel } from '@/features/reports/lib/dimension-labels'

interface GroupListCardProps {
  title: string
  subtitle: string
  dimension: ReportDimension
  rows: Array<ReportRow>
  total: ReportTotals
  measure: ReportMeasure
  detailsTo: string
  /** Router search params, so a tile hands its own slice to the page it drills into. */
  detailsSearch?: Record<string, unknown>
  /** Rendered under the list — the cash share, or the unassigned-articles warning. */
  footer?: React.ReactNode
  /** A level switch (Haupt / Unter) belonging to this card's own breakdown. */
  headerAction?: React.ReactNode
  /**
   * Show the other measure under the value: pieces beside revenue, revenue beside pieces.
   *
   * On for product groups, where "how much did food earn" and "how many meals went out" are two
   * different questions the owner asks in the same breath — a kitchen plans in portions, not in
   * euros. Off for payment methods, where a piece count says nothing: an article is not paid for
   * per piece, and the number would invite a comparison that has no meaning.
   */
  showSecondaryMeasure?: boolean
  maxRows?: number
  loading?: boolean
}

/**
 * A ranked list with an inline magnitude bar (CHG-2026-09-manager-reporting).
 *
 * One component for product groups and for payment methods, because they are the same shape — the
 * only difference is which dimension fills it, which is the point of a single report endpoint.
 *
 * The collector-category row is amber and keeps its "assign these" link. Revenue sitting in the
 * collector belongs to no real product group, so hiding the row would make every group total
 * quietly wrong by exactly the amount nobody is looking at.
 */
export function GroupListCard({
  title,
  subtitle,
  dimension,
  rows,
  total,
  measure,
  detailsTo,
  detailsSearch,
  footer,
  headerAction,
  showSecondaryMeasure = false,
  maxRows = 6,
  loading,
}: GroupListCardProps) {
  const { t } = useTranslation('reports')
  const value = (row: ReportRow) =>
    measure === 'units' ? row.units : row.revenueCents
  const totalValue = measure === 'units' ? total.units : total.revenueCents
  const format = (n: number) =>
    measure === 'units' ? formatNumber(n) : formatCurrency(n)
  const secondary = (row: ReportRow) =>
    measure === 'units'
      ? formatCurrency(row.revenueCents)
      : t('units.short', { value: formatNumber(row.units) })

  const shown = rows.slice(0, maxRows)

  return (
    <Card className="flex min-w-0 flex-col">
      <CardHeader>
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
        {headerAction ? (
          <div className="col-start-2 row-span-2 self-start">
            {headerAction}
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : shown.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t('empty')}
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {shown.map((row) => (
              <li
                key={row.key}
                className="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)_auto] items-center gap-3"
              >
                <span
                  className={
                    row.isCollector
                      ? 'flex min-w-0 items-center gap-1.5 truncate text-sm text-warning'
                      : 'min-w-0 truncate text-sm'
                  }
                  title={formatRowLabel(t, dimension, row)}
                >
                  {row.isCollector ? (
                    <TriangleAlert
                      className="h-3.5 w-3.5 shrink-0"
                      aria-hidden="true"
                    />
                  ) : null}
                  {row.isCollector
                    ? t('groups.collector')
                    : formatRowLabel(t, dimension, row)}
                </span>
                <ShareBar
                  share={totalValue > 0 ? value(row) / totalValue : 0}
                  tone={row.isCollector ? 'warning' : 'magnitude'}
                />
                <span className="text-right">
                  <span
                    className={
                      row.isCollector
                        ? 'block text-sm font-semibold tabular-nums text-warning'
                        : 'block text-sm font-semibold tabular-nums'
                    }
                  >
                    {format(value(row))}
                  </span>
                  {showSecondaryMeasure ? (
                    <span className="block text-xs tabular-nums text-muted-foreground">
                      {secondary(row)}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}

        {footer ? <div className="mt-auto">{footer}</div> : null}

        <Link
          to={detailsTo}
          search={detailsSearch}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          {t('details')}
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </Link>
      </CardContent>
    </Card>
  )
}
